<template>
  <!-- Loading State -->
  <div v-if="loading" class="flex items-center justify-center min-h-screen">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <!-- Error State -->
  <PageError v-else-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="item" class="p-6 dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back to Host Availability
        </button>
        Host Availability: {{ item.job_name }}
        <div class="relative group">
          <button
            type="button"
            class="hover:bg-transparent bg-transparent ml-2"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
    </div>

    <!-- Field table -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-md border-b border-slate-700/50">
          <tr>
            <th class="py-2 px-4" :class="hasPendingChanges ? 'w-1/4' : 'w-1/3'">Field</th>
            <th class="py-2 px-4" :class="hasPendingChanges ? 'w-2/4' : 'w-2/3'">Value</th>
            <th v-if="hasPendingChanges" class="py-2 px-4 w-1/4">Pending Value</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="field in visibleFields"
            :key="field.key"
            :id="'host-availability-field-' + field.key"
            :class="['border-b dark:border-gray-700 hover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <template v-if="editingField === field.key">
                <div class="flex items-start gap-2 w-full">
                  <GlassSelect
                    v-if="field.type === 'select'"
                    :id="field.key"
                    v-model="editValue"
                    :options="field.options"
                    placeholder="Select an option"
                  />
                  <input
                    v-else-if="field.type === 'checkbox'"
                    type="checkbox"
                    v-model="editValue"
                    class="h-5 w-5 mt-1"
                  />
                  <div v-else class="flex-grow">
                    <input
                      v-model="editValue"
                      :type="field.type"
                      @focus="clearFieldError(field.key)"
                      :class="[
                        'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                        fieldErrors[field.key]
                          ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-500',
                      ]"
                    />
                    <FieldError v-if="fieldErrors[field.key]" :message="fieldErrors[field.key]" class="mt-1" />
                  </div>
                  <div class="flex gap-2 flex-shrink-0">
                    <button @click="saveEdit(field.key)" class="ml-2 bg-green-600 hover:bg-green-700 text-white btn">Save</button>
                    <button @click="cancelEdit(field.key)" class="ml-2 bg-red-600 hover:bg-red-700 text-white btn">Cancel</button>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="flex items-center">
                  <span class="flex-grow">{{ formatValue(field.key, field.key in (item.changes || {}) ? item.changes[field.key].from : item[field.key]) }}</span>
                  <button
                    v-if="handleNoEdit(field.key)"
                    @click="startEdit(field.key)"
                    :id="field.key"
                    class="ml-2 p-1 ml-10 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              </template>
            </td>
            <td v-if="hasPendingChanges" class="py-4 px-4">
              {{ field.key in (item.changes || {}) ? formatValue(field.key, item.changes[field.key].to) : '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getHostAvailabilityById,
  updateHostAvailability,
} from "@/services/hostAvailabilityService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import { formatError, extractErrorMessage } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "HostAvailabilityDetails",
  components: {
    FieldError,
    PageError,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      fieldErrors: {},
      item: null,
      editingField: null,
      editValue: null,
      originalValue: null,
      loading: false,
      error: "",
      errorCode: "",
      canChangeHostAvailability: false,
      noEditFields: ["job_name", "pending", "changes", "last_modified", "created_date"],
      displayFields: [
        { key: "job_name", label: "Job Name", type: "text" },
        { key: "host", label: "Host", type: "text" },
        { key: "os", label: "Operating System", type: "select", options: ["Linux", "Windows"] },
        { key: "port", label: "Port", type: "text" },
        { key: "module", label: "Module", type: "select", options: ["http_2xx", "icmp", "udp", "tcp_connect"] },
        { key: "application", label: "Application", type: "text" },
        { key: "ip_addr", label: "IP Address", type: "text" },
        { key: "domain", label: "Domain", type: "text" },
        { key: "scrape_interval", label: "Scrape Interval", type: "number", staffOnly: true },
        { key: "scrape_timeout", label: "Scrape Timeout", type: "number", staffOnly: true },
        { key: "target", label: "Target", type: "text" },
        { key: "technical_contact", label: "Technical Contact", type: "text" },
        { key: "in_maintenance", label: "In Maintenance", type: "checkbox" },
        { key: "last_modified", label: "Last Modified", type: "text" },
        { key: "created_date", label: "Created Date", type: "text" },
      ],
      hostAvailabilityDetailSteps: [
        {
          element: '#host-availability-field-job_name',
          action: 'display',
          message: 'The job name identifies this host availability check.'
        },
        {
          element: '#host-availability-field-host',
          action: 'display',
          message: 'The host being monitored. Click the pencil icon to change.'
        },
        {
          element: '#host-availability-field-module',
          action: 'display',
          message: 'The module determines the check type (HTTP, ICMP, UDP, or TCP). Click the pencil icon to change.'
        },
        {
          element: '#host-availability-field-target',
          action: 'display',
          message: 'The target URL or address the check hits. Click the pencil icon to edit.'
        },
        {
          element: '#host-availability-field-technical_contact',
          action: 'display',
          message: 'The technical contact for this host. Click the pencil icon to edit.'
        },
        {
          element: '#host-availability-field-in_maintenance',
          action: 'display',
          message: 'Whether the host is in maintenance. Alerting is paused while enabled.'
        },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    visibleFields() {
      return this.displayFields.filter((f) => !f.staffOnly || useStore.isStaff());
    },
    hasPendingChanges() {
      return (
        this.item?.pending &&
        this.item?.changes &&
        Object.keys(this.item.changes).length > 0
      );
    },
  },
  async mounted() {
    const id = this.$route.params.id;
    try {
      this.loading = true;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to get host availability details!";
        this.errorCode = "422!";
        return;
      }
      this.item = await getHostAvailabilityById(id, validation.client);
      if (this.user) {
        this.canChangeHostAvailability = await useStore.hasPermission("Can change Host Availability");
      }
    } catch (error) {
      console.error("Error fetching host availability:", error);
      const { code, message } = formatError(error);
      this.errorCode = code;
      this.error = message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.hostAvailabilityDetailSteps);
    },
    clearFieldError(fieldName) {
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors["non_field_errors"];
      }
    },
    handleNoEdit(key) {
      if (this.canChangeHostAvailability || this.user?.is_superuser) {
        return !this.noEditFields.includes(key);
      }
      return false;
    },
    startEdit(key) {
      this.editingField = key;
      this.editValue =
        key in (this.item.changes || {}) ? this.item.changes[key].from : this.item[key];
      this.originalValue = this.editValue;
    },
    cancelEdit(key) {
      this.clearFieldError(key);
      this.editingField = null;
      this.item[key] = this.originalValue;
      this.editValue = null;
    },
    goBack() {
      this.$router.push({
        name: "HostAvailabilities",
        query: this.tenant.addSchemaParam(),
      });
    },
    async saveEdit(key) {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to save host availability changes!";
          this.errorCode = "422!";
          return;
        }
        this.fieldErrors = {};
        const updated = await updateHostAvailability(
          this.item.id,
          { [key]: this.editValue },
          validation.client
        );
        this.item = { ...this.item, ...updated };
        this.editingField = null;
        this.editValue = null;
      } catch (error) {
        this.item[key] = this.originalValue;
        this.fieldErrors = {};
        const response = error?.response;
        const errData = response?.data;
        const isValidationError =
          response?.status === 400 &&
          errData && typeof errData === "object" && !Array.isArray(errData);
        if (isValidationError) {
          this.displayFields.forEach((field) => {
            const fieldError = errData[field.key];
            if (fieldError) {
              this.fieldErrors[field.key] = Array.isArray(fieldError)
                ? fieldError[0]
                : fieldError;
            }
          });
          if (errData.non_field_errors) {
            this.fieldErrors[key] = Array.isArray(errData.non_field_errors)
              ? errData.non_field_errors[0]
              : errData.non_field_errors;
          } else if (errData.detail) {
            this.fieldErrors[key] = Array.isArray(errData.detail)
              ? errData.detail[0]
              : errData.detail;
          }
        } else if (response) {
          this.fieldErrors[key] = extractErrorMessage(
            error,
            "Something went wrong while saving. Please try again."
          );
        } else {
          this.fieldErrors[key] = "Network error or server unreachable. Please try again.";
        }
        console.error("Error updating host availability:", error);
      }
    },
    formatValue(key, value) {
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return value !== null && value !== undefined && value !== "" ? value : "N/A";
    },
  },
};
</script>
